import React, { useEffect, useState, useCallback, ReactText } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import {
    EInvoiceModel,
    EInvoiceRequestModel,
    InvoiceApi,
    InvoiceConfigurationModel,
    ReceiptLogModel,
    RequestEInvoiceResult,
} from '../../services/InvoiceApi'
import { ActionMenuItem, DocumentDetailsContainer } from '../../shared/documents/details/DocumentDetailsContainer'
import {
    InvoiceDocumentModel,
    CompanyPrintSettingsModel,
    TemplateTenderDocumentModel,
    PaymentMethod,
    PriceCalculationMode,
    ReceiptStatus,
    ReceiptStatusHistory,
    VersionInformation,
} from '../../shared/models'
import { InlineInputEdit } from '../../shared/forms/InlineInputEdit'
import { FormikProps, Field, useFormikContext } from 'formik'
import { InlineDateEdit } from '../../shared/forms/InlineDateEdit'
import { ClientInput } from '../../shared/documents/details/elements/client-input/ClientInput'
import { ChangePricesAction } from '../../shared/documents/details/elements/ChangePricesAction'
import Modal from '../../shared/modal'
import * as Yup from 'yup'
import { SendViaEmailModal } from '../../shared/documents/SendViaEmailModal'
import { toastError, toastSuccess, toastWarning } from '../../shared/toastr'
import { ConcreteTenderApi } from '../../services/ConcreteTenderApi'
import { OfferionTooltip } from '../../shared/tooltip'
import { CurrencyLabel } from '../../shared/ui/currency'
import { EInvoiceModal, EInvoiceModalType } from './EInvoiceModal'
import { OfferionErrorMessage } from '../../shared/forms/validation/OfferionErrorMessage'
import { ConfirmFiscalizationModal } from './ConfirmFiscalizationModal'
import dayjs from 'dayjs'
import forCharge from '../../shared/utils/forCharge'
import { date, getKeyForReceiptStatus } from '../../shared/utils/formatters'
import {
    faCog,
    faEnvelope,
    faFileContract,
    faFileMedical,
    faHistory,
    faPrint,
    faStream,
    faTimes,
    faTrashAlt,
    faPaperPlane,
} from '@fortawesome/free-solid-svg-icons'
import { ChangeStatusModal } from '../../shared/documents/ChangeStatusModal'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { openDocument } from '../../shared/utils/openDocument'
import { useOfferionModal } from '../../shared/modal/ConfirmationModal'
import { QuickActionItem, QuickActionSubtitle, QuickActionTitle } from '../../shared/documents/QuickActions'
import { Checkbox } from '../ui/Checkbox'
import { InlineTextareaEdit } from '../../shared/forms/InlineTextareaEdit'
import { ClientApi } from '../../services/ClientApi'
import { FiscalizationApi } from '../../services/FiscalizationApi'
import { isLawInEffect } from '../../shared/utils/dates'
import { validateEInvoiceItems, validateEInvoicePreOpenForDocument } from '../../shared/utils/eInvoicePrechecks'
import { validateOib } from '../../shared/utils/validators'
import { useCompanyStore } from '../../shared/store/companyStore'

//if obrt contains word "obrt" then we treat it as small business, otherwise as physical person (by client request)
function isObrtName(name?: string | null): boolean {
    if (!name) return false
    return /\bobrt\b/i.test(name)
}

function getAutofillFlags(data: { legalFormShort?: string | null }) {
    const legalFormEmpty = !data.legalFormShort || data.legalFormShort.trim() === ''
    return { legalFormEmpty }
}

const InvoiceLogItem = ({ item, isFirst }: { item: ReceiptLogModel; isFirst: boolean }) => {
    const { t } = useOfferionTranslation()
    if (item.status == ReceiptStatusHistory.Draft && isFirst) {
        if (item.additionalInformation)
            return (
                <>
                    {t('Invoice.Log.invoiceCreated', { offerNumber: item.additionalInformation })} ({item.userFullName})
                </>
            )
        else
            return (
                <>
                    {t('Invoice.Log.documentCreated')} ({item.userFullName})
                </>
            )
    }

    if (item.status == ReceiptStatusHistory.Fiscalized)
        return (
            <>
                {t('Invoice.Log.fiscalized')} ({item.userFullName})
            </>
        )

    if (item.showOnlyStatusChange)
        return (
            <>
                {t('Invoice.Log.StatusChanged', { status: t(getKeyForReceiptStatus(item.status)) })} (
                {item.userFullName || t('Invoice.Log.System')})
            </>
        )

    if (item.status == ReceiptStatusHistory.Sent)
        return (
            <>
                {t('Invoice.Log.documentSent', { mailAddress: item.additionalInformation })} ({item.userFullName || t('Invoice.Log.System')}
                )
            </>
        )

    if (item.status == ReceiptStatusHistory.SentEInvoice)
        return (
            <>
                {t('Invoice.Log.documentSentAsEInvoice', { mailAddress: item.additionalInformation })} (
                {item.userFullName || t('Invoice.Log.System')})
            </>
        )

    return (
        <>
            {t('Invoice.Log.StatusChanged', { status: t(getKeyForReceiptStatus(item.status)) })} (
            {item.userFullName || t('Invoice.Log.System')})
        </>
    )
}

export const InvoiceLogModal = ({ close, invoiceId }: { close: () => void; invoiceId: ReactText | undefined }) => {
    const { t } = useOfferionTranslation()
    const [logs, setLogs] = useState<ReceiptLogModel[] | undefined>()

    useEffect(() => {
        if (!invoiceId) return

        InvoiceApi.getLogs(invoiceId).then(result => {
            setLogs(result)
        })
    }, [])

    if (logs == undefined) return null

    return (
        <div className="popup__overlay popup__overlay--show send-document-by-email" style={{ cursor: 'default' }}>
            <div className="popup" style={{ maxWidth: '550px' }}>
                <h1 className="overlay__title">{t('Invoice.Log.pageTitle')}</h1>
                <div className="popup__close icono-cross" style={{ cursor: 'pointer' }} onClick={close}>
                    {t('Allaround.Popup.close')}
                </div>
                <ul style={{ padding: 0 }}>
                    {logs.map((l, i) => {
                        return (
                            <li>
                                {date(l.createdAt)} <InvoiceLogItem item={l} isFirst={i == 0} />
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

export const InvoiceDetailsContainer = () => {
    const params = useParams<{ invoiceId: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useOfferionTranslation()

    const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false)
    const [eInvoiceModalType, setEInvoiceModalType] = useState<EInvoiceModalType | null>(null)
    const [eInvoiceData, setEInvoiceData] = useState<EInvoiceModel | null>(null)
    const [configuration, setConfiguration] = useState<InvoiceConfigurationModel | undefined>()
    const [isLogModalVisible, setIsLogModalVisible] = useState(false)
    const [changeStatusModalVisible, setChangeStatusModalVisible] = useState(false)
    const [confirmFiscalizationPopupVisible, setConfirmFiscalizationPopupVisible] = useState(false)
    const offerionModal = useOfferionModal()
    const companyCountry = useCompanyStore(state => state.companySettings.country)

    const [shouldFiscalizeDefault, setShouldFiscalizeDefault] = useState(false)
    const [oibDataFound, setOibDataFound] = useState<boolean | null>(null)
    const [initialShouldNotFiscalize, setInitialShouldNotFiscalize] = useState<boolean>(false)
    const [showNewFiscalizeMessage, setShowNewFiscalizeMessage] = useState<boolean>(false)
    const [isObrtDetected, setIsObrtDetected] = useState<boolean>(false)
    const [isLegalFormEmpty, setIsLegalFormEmpty] = useState<boolean>(false)
    const [isNumberFormatInvalid, setIsNumberFormatInvalid] = useState<boolean>(false)
    const isCreateFlow = location.state?.createFlow
    const pageTitle = t(!isCreateFlow ? 'Invoice.Header.pageTitleEditInvoice' : 'Invoice.Header.pageTitleNewInvoice')

    const isWithinFiscalWindowFor = (officialDate: Date | string) => {
        const dayJsOfficialDate = dayjs(officialDate).add(3, 'day').startOf('day')
        const today = dayjs(new Date()).startOf('day')
        return dayJsOfficialDate.isAfter(today)
    }

    const openEmailModal = async (document: InvoiceDocumentModel) => {
        if (!configuration) return

        const isAccountPaymentMethod = document.details.paymentMethod === PaymentMethod.Account
        const isAfterLawChange = isLawInEffect()

        // If already fiscalized or fiscalization not active, just open modal without fiscalize option
        if (document.fiscalization.isFiscalized || !configuration.isFiscalizationActive) {
            setOibDataFound(null)
            setIsNumberFormatInvalid(false)
            setShouldFiscalizeDefault(false)
            setSendEmailModalVisible(true)
            return
        }

        // Check invoice number format against registered sale places
        let numberFormatInvalid = false
        try {
            const numCheck = await FiscalizationApi.canFiscalizeByNumber(document.details.id)
            numberFormatInvalid = !numCheck.isValid
        } catch (e) {
            console.error('Failed to check invoice number for fiscalization (email)', e)
        }
        setIsNumberFormatInvalid(numberFormatInvalid)

        let shouldFiscalize = false

        if (isAccountPaymentMethod) {
            const oib = document.client ? document.client.oib : null

            if (oib) {
                try {
                    const res = await ClientApi.getAutofill(oib)
                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(document.client?.name)
                    setIsObrtDetected(isObrt)
                    setIsLegalFormEmpty(legalFormEmpty)

                    if (res.dataFound) {
                        if (isObrt) {
                            setOibDataFound(false)
                        } else if (legalFormEmpty) {
                            setOibDataFound(false)
                            shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                        } else {
                            setOibDataFound(true)
                        }
                    } else {
                        setOibDataFound(false)
                        setIsObrtDetected(false)
                        setIsLegalFormEmpty(true)
                        shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                    }
                } catch (e) {
                    console.error('Failed to check OIB for fiscalization', e)
                    setOibDataFound(false)
                    setIsObrtDetected(false)
                    setIsLegalFormEmpty(true)
                    shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                }
            } else {
                setOibDataFound(false)
                setIsObrtDetected(false)
                setIsLegalFormEmpty(true)
                shouldFiscalize = true
            }
        } else {
            // CASH / NON-ACCOUNT PAYMENTS
            const oib = document.client ? document.client.oib : null

            if (isAfterLawChange && oib) {
                try {
                    const res = await ClientApi.getAutofill(oib)
                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(document.client?.name)
                    setIsLegalFormEmpty(legalFormEmpty)
                    setIsObrtDetected(isObrt)

                    if (isObrt) {
                        setOibDataFound(!!res.dataFound)
                    } else if (legalFormEmpty) {
                        setOibDataFound(false)
                        shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                    } else {
                        setOibDataFound(res.dataFound)
                        shouldFiscalize = res.dataFound ? false : isWithinFiscalWindowFor(document.details.officialDate)
                    }
                } catch (e) {
                    console.error('Failed to check OIB for fiscalization (cash)', e)
                    setOibDataFound(false)
                    shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                }
            } else {
                // BEFORE law change or cash without OIB: keep existing behavior
                shouldFiscalize = isWithinFiscalWindowFor(document.details.officialDate)
                setOibDataFound(false)
            }
        }

        setShouldFiscalizeDefault(shouldFiscalize)
        setSendEmailModalVisible(true)
    }

    useEffect(() => {
        InvoiceApi.getConfiguration(params.invoiceId).then(config => {
            if (config.showVatAccountingWarning) {
                if (config.isAccountingVat) {
                    toastWarning('Invoice.Header.VATIsNowOff.warningMessage')
                } else {
                    toastWarning('Invoice.Header.VATIsNowOn.warningMessage')
                }
            }

            if (config.showPriceCalculationWarning) {
                if (config.enterFullPriceEnabled) {
                    toastWarning('Invoice.Header.TotalPriceIsNowOff.warningMessage')
                } else {
                    toastWarning('Invoice.Header.TotalPriceIsNowOn.warningMessage')
                }
            }

            setConfiguration(config)
        })
    }, [params.invoiceId])

    const cancelInvoice = () => {
        offerionModal.open('Invoice.CancelInvoice.pageTitle', 'Invoice.CancelInvoice.questionYesNo', async () => {
            if (!params.invoiceId) return

            const newInvoiceId = await InvoiceApi.cancel(params.invoiceId)

            navigate(`/invoice/${newInvoiceId}`)
        })
    }

    const onCancelButtonClick = (document: InvoiceDocumentModel) => {
        if (!isCreateFlow || document.items.length) {
            navigate(-1)
        } else {
            offerionModal.open(
                'Invoice.DeleteInvoice.pageTitle',
                'Invoice.Header.NewDocumentCancelled.questionYesNo',
                async () => {
                    if (!params.invoiceId) return

                    await InvoiceApi.delete(params.invoiceId)

                    toastSuccess('Invoice.DeleteInvoice.Message.SuccessDelete')

                    navigate(`/invoices`)
                },
                undefined,
                async () => navigate(-1)
            )
        }
    }

    const deleteInvoice = (document: InvoiceDocumentModel) => {
        offerionModal.open(
            'Invoice.DeleteInvoice.pageTitle',
            'Invoice.DeleteInvoice.questionYesNo',
            async () => {
                if (!params.invoiceId) return

                await InvoiceApi.delete(params.invoiceId)

                toastSuccess('Invoice.DeleteInvoice.Message.SuccessDelete')

                navigate(`/invoices`)
            },
            { invoiceNumber: document.details.number }
        )
    }

    const getDocumentFinal = (document: InvoiceDocumentModel, shouldFiscalize: boolean) => {
        if (!params.invoiceId) return Promise.resolve(false)

        return InvoiceApi.getDocument(params.invoiceId, shouldFiscalize).then((data: any) => {
            openDocument(`${t('Invoice.Print.invoice')} ${document.details.number}.pdf`, data)
            setConfirmFiscalizationPopupVisible(false)

            return false
        })
    }

    const getDocumentStep1 = async (document: InvoiceDocumentModel) => {
        if (!configuration)
            return {
                openModal: false,
                oibDataFound: null as boolean | null,
                initialShouldNotFiscalize: false,
                showNewFiscalizeMessage: false,
                numberFormatError: false,
            }

        const isAccountPaymentMethod = document.details.paymentMethod === PaymentMethod.Account

        // If already fiscalized, fiscalization not active or e-invoice already sent, just print
        if (document.fiscalization.isFiscalized || !configuration.isFiscalizationActive || document.details.eInvoiceSent) {
            await getDocumentFinal(document, false)
            return {
                openModal: false,
                oibDataFound: null,
                initialShouldNotFiscalize: false,
                showNewFiscalizeMessage: false,
                numberFormatError: false,
            }
        }

        // Check invoice number format against registered sale places
        let numberFormatInvalid = false
        try {
            const numCheck = await FiscalizationApi.canFiscalizeByNumber(document.details.id)
            numberFormatInvalid = !numCheck.isValid
        } catch (e) {
            console.error('Failed to check invoice number for fiscalization', e)
        }

        if (isAccountPaymentMethod) {
            const oib = document.client ? document.client.oib : null
            if (oib) {
                try {
                    const res = await ClientApi.getAutofill(oib)
                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(document.client?.name)

                    if (res.dataFound) {
                        if (isObrt) {
                            await getDocumentFinal(document, false)
                            return {
                                openModal: false,
                                oibDataFound: false,
                                initialShouldNotFiscalize: true,
                                showNewFiscalizeMessage: false,
                                numberFormatError: false,
                            }
                        }
                        if (!legalFormEmpty) {
                            await getDocumentFinal(document, false)
                            return {
                                openModal: false,
                                oibDataFound: true,
                                initialShouldNotFiscalize: true,
                                showNewFiscalizeMessage: false,
                                numberFormatError: false,
                            }
                        }
                        // legal form empty, allow modal
                        return {
                            openModal: true,
                            oibDataFound: false,
                            initialShouldNotFiscalize: numberFormatInvalid,
                            showNewFiscalizeMessage: false,
                            numberFormatError: numberFormatInvalid,
                        }
                    }

                    // OIB not found, allow modal with info
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: !numberFormatInvalid,
                        numberFormatError: numberFormatInvalid,
                    }
                } catch (e) {
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }
            }

            return {
                openModal: true,
                oibDataFound: false,
                initialShouldNotFiscalize: numberFormatInvalid,
                showNewFiscalizeMessage: !numberFormatInvalid,
                numberFormatError: numberFormatInvalid,
            }
        }

        // Non-transactional (cash/other) flow
        const hasOib = !!(document.client && document.client.oib)
        const isAfterLawChange = isLawInEffect()

        if (isAfterLawChange && hasOib) {
            try {
                const res = await ClientApi.getAutofill(document.client!.oib!)
                const { legalFormEmpty } = getAutofillFlags(res)
                const isObrt = isObrtName(document.client?.name)

                if (isObrt) {
                    return {
                        openModal: true,
                        oibDataFound: !!res.dataFound,
                        initialShouldNotFiscalize: true,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }

                if (legalFormEmpty) {
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }

                return {
                    openModal: true,
                    oibDataFound: !!res.dataFound,
                    initialShouldNotFiscalize: numberFormatInvalid || !!res.dataFound,
                    showNewFiscalizeMessage: false,
                    numberFormatError: numberFormatInvalid,
                }
            } catch (e) {
                return {
                    openModal: true,
                    oibDataFound: false,
                    initialShouldNotFiscalize: numberFormatInvalid,
                    showNewFiscalizeMessage: false,
                    numberFormatError: numberFormatInvalid,
                }
            }
        }

        return {
            openModal: true,
            oibDataFound: false,
            initialShouldNotFiscalize: numberFormatInvalid,
            showNewFiscalizeMessage: false,
            numberFormatError: numberFormatInvalid,
        }
    }

    const emailInvoice = (info: {
        toEmail: string
        toEmailsCc: string
        emailBody: string
        includeCopyToSelf: boolean
        shouldFiscalize: boolean
    }) => {
        return InvoiceApi.sendInvoice(
            params.invoiceId,
            info.toEmail,
            info.toEmailsCc,
            info.emailBody,
            info.includeCopyToSelf,
            info.shouldFiscalize
        ).then(() => setSendEmailModalVisible(false))
    }

    const showEInvoiceModal = async (doc: InvoiceDocumentModel) => {
        if (doc.fiscalization.isFiscalized) {
            toastWarning('Invoice.SendEInvoice.Message.cannotSendFiscalized')
            return
        }

        let latestConfiguration: InvoiceConfigurationModel
        let einvoice: EInvoiceModel

        try {
            ;[latestConfiguration, einvoice] = await Promise.all([
                InvoiceApi.getConfiguration(params.invoiceId),
                InvoiceApi.getEInvoiceData(doc.details.id, doc.details.clientId || undefined),
            ])
        } catch (e) {
            console.error('Failed to load configuration or e-invoice data for e-invoice', e)
            return
        }

        if (!latestConfiguration.isIbanValid) {
            toastWarning('Invoice.SendEInvoice.Message.warningInvalidIban')
        }
        const ok = validateEInvoicePreOpenForDocument({ missingEInvoiceData: latestConfiguration.missingEInvoiceData }, doc, t)
        if (!ok) return

        if (!validateEInvoiceItems(doc.items, t)) return

        const missingEInvoiceData = latestConfiguration.missingEInvoiceData

        const missingKeysForCompany: string[] = []

        if (missingEInvoiceData.companyOib) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.taxID')
        if (missingEInvoiceData.contactEmail) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.email')
        if (missingEInvoiceData.street) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.street')
        if (missingEInvoiceData.streetNumber) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.streetNumber')
        if (missingEInvoiceData.city) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.city')
        if (missingEInvoiceData.postalCode) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.zipCode')
        if (missingEInvoiceData.country) missingKeysForCompany.push('Invoice.SendEInvoice.Message.MissingData.country')

        if (missingKeysForCompany.length) {
            let finalMessage: string = t(missingKeysForCompany[0])

            if (missingKeysForCompany.length > 1) {
                for (let i = 1; i < missingKeysForCompany.length; i++) {
                    if (i === missingKeysForCompany.length - 1) {
                        finalMessage += ` ${t('Invoice.SendEInvoice.Message.MissingData.lastConjuction')} ${t(missingKeysForCompany[i])}`
                    } else {
                        finalMessage += `, ${t(missingKeysForCompany[i])}`
                    }
                }
            }

            toastWarning('Settings.EInvoice.Messages.warningMissingCompanyData', undefined, undefined, {
                missingCompanyData: finalMessage,
            })

            return
        }

        if (!doc.client) {
            toastWarning('Invoice.SendEInvoice.Message.noClient')

            return
        }

        const missingKeysForClient: string[] = []

        if (!doc.client.oib) {
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.taxID')
        } else if (doc.client.country === 'HR' && !validateOib(doc.client.oib)) {
            // Only validate OIB format for Croatian clients
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.validtaxID')
        }
        if (!doc.client.email && !latestConfiguration.isCroatianCompany)
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.email')
        if (!doc.client.address) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.street')
        if (!doc.client.city) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.city')
        if (!doc.client.postalCode) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.zipCode')
        if (!doc.client.country) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.country')

        if (missingKeysForClient.length) {
            let finalMessage: string = t(missingKeysForClient[0])

            if (missingKeysForClient.length > 1) {
                for (let i = 1; i < missingKeysForClient.length; i++) {
                    if (i === missingKeysForClient.length - 1) {
                        finalMessage += ` ${t('Invoice.SendEInvoice.Message.MissingData.lastConjuction')} ${t(missingKeysForClient[i])}`
                    } else {
                        finalMessage += `, ${t(missingKeysForClient[i])}`
                    }
                }
            }

            toastWarning('Invoice.SendEInvoice.Message.noClientData', undefined, undefined, {
                missingClientData: finalMessage,
            })

            return
        }

        const isAfterLawChange = isLawInEffect()

        if (isAfterLawChange) {
            // Validate classification codes based on e-invoice data
            const defaultClassificationCode = einvoice.defaultClassificationCode
            const isDefaultClassificationCodeEmpty = !defaultClassificationCode || !defaultClassificationCode.trim()
            const hasItemWithEmptyClassificationCode = einvoice.classificationCodeItems.some(
                rc => !rc.classificationCode || !rc.classificationCode.trim()
            )
            const isExemptProcess =
                einvoice.processIdentificator === 'P4' || einvoice.processIdentificator === 'P9' || einvoice.processIdentificator === 'P10'

            if (isDefaultClassificationCodeEmpty && hasItemWithEmptyClassificationCode && !isExemptProcess) {
                // Required classification codes are missing; do not open the modal
                const itemNames = einvoice.classificationCodeItems
                    .filter(rc => !rc.classificationCode || !rc.classificationCode.trim())
                    .map(rc => rc.name)
                    .join(', ')

                toastWarning('Allaround.Validation.Fiscalization.eInvoiceWrongClassificationCodeIntroBeforeModal', undefined, undefined, {
                    itemNames,
                })

                return
            }
        }

        setEInvoiceData(einvoice)

        setEInvoiceModalType(latestConfiguration.isEInvoiceConfigured ? EInvoiceModalType.SendEinvoice : EInvoiceModalType.DownloadEInvoice)
    }

    const documentPromiseFactory = useCallback(
        (isReload: boolean) => {
            if (params.invoiceId)
                return Promise.all([
                    InvoiceApi.get(params.invoiceId),
                    InvoiceApi.getEInvoiceData(params.invoiceId).catch(error => {
                        console.error('Failed to load e-invoice delivery data', error)
                        return null
                    }),
                ]).then(([invoice, latestEInvoiceData]) => {
                    setEInvoiceData(latestEInvoiceData)

                    if (isReload) return invoice

                    if (invoice.details.isCancelled) toastWarning('Invoice.Header.CancelledInvoice.warningMessage')
                    else if (invoice.fiscalization.isFiscalized) toastWarning('Invoice.Header.FiscalizedInvoice.warningMessage')

                    return invoice
                })
            else
                return InvoiceApi.getConfiguration(params.invoiceId).then(configuration => {
                    return {
                        client: null,
                        details: {
                            cultureName: null,
                            paymentMethod: configuration.newReceiptPaymentMethod,
                            printPaymentCode: configuration.newReceiptPrintPaymentCode,
                            status: ReceiptStatus.Draft,
                            id: 0,
                            number: configuration.nextReceiptNumber,
                            documentCurrency: configuration.newReceiptCurrency,
                            clientCurrency: configuration.newReceiptClientCurrency,
                            priceCalculationMode: configuration.enterFullPriceEnabled
                                ? PriceCalculationMode.FullPrice
                                : PriceCalculationMode.BasePrice,
                            documentCurrencyRate: configuration.documentCurrencyRate,
                            clientCurrencyRate: configuration.clientCurrencyRate,
                            note: configuration.newReceiptNote,
                            officialDate: new Date(),
                            paymentDateOffset: 15,
                            versionInformation: VersionInformation.Latest,
                        },
                        fiscalization: {
                            isFiscalized: false,
                            isInsideFiscalizationInterval: true,
                        },
                        items: [],
                    }
                })
        },
        [params.invoiceId]
    )

    const upsertDocument = (document: InvoiceDocumentModel) => {
        return InvoiceApi.upsert(document).then(newDocument => {
            return newDocument
        })
    }

    const createOffer = async (document: InvoiceDocumentModel) => {
        const result = await ConcreteTenderApi.createFromReceipt(document.details.id)

        if (result.id) {
            navigate(`/offer/${result.id}`, { state: { createFlow: true } })
        } else {
            const message = t('Allaround.Message.packageLimitReached', {
                offersOrInvoicesGenitiv: t('Allaround.Message.offersGenitiv'),
            })

            toastWarning(message, true)
        }
    }

    const createTemplate = (document: InvoiceDocumentModel) => {
        const template: TemplateTenderDocumentModel = {
            client: document.client ? { ...document.client, id: 0, hideOnListing: true, sourceClientId: null } : null,
            items: document.items.map(i => {
                return { ...i, id: 0 }
            }),
            details: {
                ...document.details,
                id: 0,
                paymentNoteForReceipt: document.details.note,
                paymentNoteForTender: '',
                templateName: '',
            },
        }

        navigate('/template-offer/new-from-invoice', { state: template })
    }

    const requestEInvoice = (document: InvoiceDocumentModel, data: EInvoiceRequestModel) => {
        return InvoiceApi.requestEInvoice(data).then(result => {
            if (result.status === RequestEInvoiceResult.Error) {
                toastError(result.data, false, result.details)

                return
            }

            if (eInvoiceModalType === EInvoiceModalType.DownloadEInvoice || data.downloadAfterSend) {
                openDocument(`eRačun ${document.details.number}.xml`, result.data, 'application/xml')
            } else {
                toastSuccess('Invoice.SendEInvoice.Message.success')
            }

            setEInvoiceModalType(null)
        })
    }

    const schema = Yup.object<InvoiceDocumentModel>().shape({
        details: Yup.object().shape({
            paymentDateOffset: Yup.number()
                .nullable()
                .moreThan(-1, t('Allaround.Validation.paymentDateInFuture', { fieldName: t('Invoice.Table.DateDue.title') })),
        }),
    })

    if (!configuration) return null

    const ActionComponent = ({
        document,
        actions: { reloadDocument },
        openSettingsModal,
    }: {
        document: InvoiceDocumentModel
        actions: { reloadDocument: () => Promise<void> }
        openSettingsModal: () => void
    }) => {
        const formik = useFormikContext<InvoiceDocumentModel>()

        const isWithinFiscalWindow = () => isWithinFiscalWindowFor(document.details.officialDate)

        const canFiscalize = () => {
            if (isNumberFormatInvalid) return false
            if (document.details.eInvoiceSent) return false
            if (document.fiscalization.isFiscalized || !configuration.isFiscalizationActive) return false
            if (!isWithinFiscalWindow()) return false
            if (document.details.paymentMethod === PaymentMethod.Account) {
                return !isObrtDetected && (isLegalFormEmpty || oibDataFound === false)
            }
            return true
        }

        return (
            <>
                <ActionMenuItem
                    id="Invoice.Header.Menu.print"
                    icon={faPrint}
                    onClick={() =>
                        getDocumentStep1(document).then(res => {
                            setOibDataFound(res.oibDataFound)
                            const withinWindow = isWithinFiscalWindowFor(document.details.officialDate)
                            setInitialShouldNotFiscalize(withinWindow ? res.initialShouldNotFiscalize : true)
                            setShowNewFiscalizeMessage(res.showNewFiscalizeMessage)
                            setIsNumberFormatInvalid(res.numberFormatError)
                            setConfirmFiscalizationPopupVisible(res.openModal)
                        })
                    }
                    labelKey="Invoice.Header.Menu.print"
                ></ActionMenuItem>
                <Modal isOpen={confirmFiscalizationPopupVisible}>
                    <ConfirmFiscalizationModal
                        officialDate={document.details.officialDate}
                        clientOib={document.client?.oib || ''}
                        clientName={document.client?.name || ''}
                        eInvoiceSent={document.details.eInvoiceSent}
                        oibDataFound={oibDataFound}
                        initialShouldNotFiscalize={initialShouldNotFiscalize}
                        showNewFiscalizeMessage={showNewFiscalizeMessage}
                        numberFormatError={isNumberFormatInvalid}
                        close={() => setConfirmFiscalizationPopupVisible(false)}
                        onSubmit={shouldFiscalize =>
                            getDocumentFinal(document, shouldFiscalize).then(result => {
                                setConfirmFiscalizationPopupVisible(result)

                                if (shouldFiscalize) reloadDocument()
                            })
                        }
                    ></ConfirmFiscalizationModal>
                </Modal>
                <ActionMenuItem
                    id="Invoice.Header.Menu.sendMail"
                    icon={faEnvelope}
                    onClick={() => openEmailModal(document)}
                    labelKey="Invoice.Header.Menu.sendMail"
                ></ActionMenuItem>

                <Modal isOpen={sendEmailModalVisible}>
                    <SendViaEmailModal
                        pageTitleKey="Invoice.Send.pageTitleInvoice"
                        subTitleKey="Invoice.Send.invoiceAttached"
                        initialFiscalizationValue={shouldFiscalizeDefault}
                        defaultMailText={configuration.defaultMailText}
                        clientEmail={document.client ? document.client.email : ''}
                        documentDetails={() => (
                            <span className="invoice-info">
                                <ul>
                                    <li>{document.details.number}</li>
                                    <li>{document.client ? document.client.name : null}</li>
                                    <li>
                                        <CurrencyLabel
                                            amount={forCharge(configuration.enterFullPriceEnabled, document.items)}
                                            currency={document.details.documentCurrency}
                                        />
                                    </li>
                                    <li>{document.details.officialDate ? `${date(document.details.officialDate)}` : null}</li>
                                </ul>
                            </span>
                        )}
                        documentFooterRenderer={() => {
                            if (isNumberFormatInvalid) {
                                return (
                                    <div className="info__row" style={{ marginTop: 6, color: 'red' }}>
                                        <small>{t('Invoice.Print.Fiscalisation.invalidNumberFormat')}</small>
                                    </div>
                                )
                            }
                            return canFiscalize() ? (
                                <>
                                    <div className="info__row flex">
                                        <Checkbox name="shouldFiscalize" />
                                        <div className="ml1">
                                            <label htmlFor="shouldFiscalize">{t('Invoice.Send.fiscalizeYesNo')}</label>
                                        </div>
                                    </div>

                                    {(() => {
                                        if (oibDataFound === false) {
                                            return (
                                                <div className="info__row" style={{ marginTop: 6 }}>
                                                    <small>{t('Invoice.Print.Fiscalisation.messageNoOibFound')}</small>
                                                </div>
                                            )
                                        }

                                        if (oibDataFound === true) {
                                            return (
                                                <div className="info__row" style={{ marginTop: 6 }}>
                                                    <small>{t('Invoice.Print.Fiscalisation.messageOibCroatianCompany')}</small>
                                                </div>
                                            )
                                        }

                                        return null
                                    })()}
                                </>
                            ) : null
                        }}
                        close={() => setSendEmailModalVisible(false)}
                        onSubmit={values =>
                            emailInvoice({ ...values, shouldFiscalize: canFiscalize() && values.shouldFiscalize }).then(() => {
                                if (values.shouldFiscalize) reloadDocument()
                            })
                        }
                    />
                </Modal>
                <ActionMenuItem
                    id="Invoice.Header.Menu.invoiceSettings"
                    icon={faCog}
                    onClick={openSettingsModal}
                    allowActionIfNotSaved={true}
                    allowActionWithoutAnyItems={true}
                    labelKey="Invoice.Header.Menu.invoiceSettings"
                ></ActionMenuItem>
                {!document.details.cancelledFromReceiptId && !document.details.isCancelled && (
                    <ActionMenuItem icon={faTimes} onClick={cancelInvoice} labelKey="Invoice.Header.Menu.cancelInvoice"></ActionMenuItem>
                )}
                <ActionMenuItem
                    id="Invoice.Header.Menu.sendEInvoice"
                    icon={faPaperPlane}
                    onClick={() => showEInvoiceModal(formik.values)}
                    labelKey="Invoice.Header.Menu.sendEInvoice"
                ></ActionMenuItem>
                {eInvoiceModalType != null && eInvoiceData && (
                    <Modal isOpen={eInvoiceModalType != null}>
                        <EInvoiceModal
                            details={{
                                forCharge: forCharge(configuration.enterFullPriceEnabled, document.items),
                                clientEmail: document.client ? document.client.email : '',
                                creator: document.creator ? `${document.creator.firstName} ${document.creator.lastName}` : '',
                                clientName: document.client ? document.client.name : '',
                                currency: document.details.documentCurrency,
                                documentNumber: document.details.number,
                                invoiceId: document.details.id,
                                officialDate: document.details.officialDate,
                            }}
                            eInvoiceData={eInvoiceData}
                            hasTaxFreeItems={document.items.some(i => i.taxFree)}
                            close={() => setEInvoiceModalType(null)}
                            onSubmit={res =>
                                requestEInvoice(document, { ...res }).finally(() => {
                                    reloadDocument().catch(e => console.error('Failed to reload document after e-invoice request', e))
                                })
                            }
                            type={eInvoiceModalType}
                        ></EInvoiceModal>
                    </Modal>
                )}
                <ActionMenuItem
                    id="Invoice.Side.changeStatus"
                    icon={faStream}
                    onClick={() => setChangeStatusModalVisible(true)}
                    labelKey="Invoice.Side.changeStatus"
                ></ActionMenuItem>
                <Modal isOpen={changeStatusModalVisible}>
                    <ChangeStatusModal
                        mapping="ReceiptStatus"
                        currentStatus={document.details.status}
                        paymentDate={document.details.paymentDate}
                        onSubmit={async (newStatus, newPaymentDate) => {
                            formik.setValues({
                                ...document,
                                details: {
                                    ...document.details,
                                    status: newStatus,
                                    paymentDate: newPaymentDate,
                                },
                            })

                            await formik.submitForm()

                            setChangeStatusModalVisible(false)
                        }}
                        pageTitleKey="Invoice.ChangeStatus.pageTitle"
                        close={() => setChangeStatusModalVisible(false)}
                        options={[
                            { value: 0, translationKey: 'Allaround.Status.draft' },
                            { value: 1, translationKey: 'Allaround.Status.invoiceSent' },
                            { value: 3, translationKey: 'Allaround.Status.invoicePaid' },
                        ]}
                        showPaymentDate={status => status === ReceiptStatus.Paid}
                    />
                </Modal>

                {!document.details.cancelledFromReceiptId && (
                    <ActionMenuItem
                        id="Invoice.Header.Menu.createOffer"
                        icon={faFileMedical}
                        onClick={() => createOffer(document)}
                        labelKey="Invoice.Header.Menu.createOffer"
                    ></ActionMenuItem>
                )}
                {!document.details.cancelledFromReceiptId && (
                    <ActionMenuItem
                        id="Invoice.Header.Menu.makeTemplate"
                        icon={faFileMedical}
                        onClick={() => createTemplate(document)}
                        labelKey="Invoice.Header.Menu.makeTemplate"
                    ></ActionMenuItem>
                )}
                <ChangePricesAction enterFullPriceEnabled={configuration.enterFullPriceEnabled}></ChangePricesAction>
                <ActionMenuItem
                    id="Invoice.Header.Menu.log"
                    icon={faHistory}
                    onClick={() => setIsLogModalVisible(true)}
                    allowActionWithoutAnyItems={true}
                    labelKey="Invoice.Header.Menu.log"
                ></ActionMenuItem>
                <Modal isOpen={isLogModalVisible}>
                    <InvoiceLogModal invoiceId={params.invoiceId} close={() => setIsLogModalVisible(false)} />
                </Modal>
                {!document.details.isCancelled && !document.fiscalization.isFiscalized && (
                    <ActionMenuItem
                        id="Invoice.Header.Menu.delete"
                        icon={faTrashAlt}
                        allowActionWithoutAnyItems={true}
                        onClick={() => deleteInvoice(document)}
                        labelKey="Invoice.Header.Menu.delete"
                    ></ActionMenuItem>
                )}
            </>
        )
    }

    const QuickActionsComponent = ({ document }: { document: InvoiceDocumentModel }) => {
        return (
            <>
                <QuickActionTitle id="Invoice.Side.actionsTitle" text={t('Invoice.Side.actionsTitle')} />
                <QuickActionSubtitle text={t('Invoice.Side.actionsDescription')} />
                <QuickActionItem
                    onClick={() =>
                        getDocumentStep1(document).then(res => {
                            setOibDataFound(res.oibDataFound)
                            const withinWindow = isWithinFiscalWindowFor(document.details.officialDate)
                            setInitialShouldNotFiscalize(withinWindow ? res.initialShouldNotFiscalize : true)
                            setShowNewFiscalizeMessage(res.showNewFiscalizeMessage)
                            setIsNumberFormatInvalid(res.numberFormatError)
                            setConfirmFiscalizationPopupVisible(res.openModal)
                        })
                    }
                    icon={faPrint}
                    text={t('Invoice.Side.print')}
                    id="Invoice.Side.print"
                />
                <QuickActionItem
                    onClick={() => openEmailModal(document)}
                    icon={faEnvelope}
                    text={t('Invoice.Side.sendMail')}
                    id="Invoice.Side.sendMail"
                />
                {!document.details.cancelledFromReceiptId && !document.details.isCancelled && (
                    <>
                        {companyCountry === 'HR' && (
                            <QuickActionItem
                                onClick={() => showEInvoiceModal(document)}
                                icon={faPaperPlane}
                                text={t('Invoice.Side.sendEInvoice')}
                                id="Invoice.Side.sendEInvoice"
                            />
                        )}
                        <QuickActionItem
                            onClick={cancelInvoice}
                            icon={faTimes}
                            text={t('Invoice.Side.cancelInvoice')}
                            id="Invoice.Side.cancelInvoice"
                        />
                    </>
                )}
            </>
        )
    }

    const FooterComponent = ({ document, formik }: { document: InvoiceDocumentModel; formik: FormikProps<InvoiceDocumentModel> }) => {
        return (
            <div className="notice__container notice__container--with-margin">
                <h2 className="notice__title">{t('Invoice.Table.Footer.remark')}</h2>
                <div className="notice__content remark-placeholder">
                    <InlineTextareaEdit
                        currentValue={document.details.note}
                        preventSubmitOnEnter={true}
                        className="ng-inline-edit__text"
                        placeholderKey="Invoice.Table.Footer.remarkPlaceholder"
                        onSave={({ newValue }) => {
                            formik.setFieldValue(`details.note`, newValue)
                            formik.submitForm()
                        }}
                    />
                </div>
            </div>
        )
    }

    const HeaderComponent = ({
        document,
        formik,
        rateComponent,
    }: {
        document: InvoiceDocumentModel
        formik: FormikProps<InvoiceDocumentModel>
        rateComponent: JSX.Element
    }) => {
        let dueDate: Date | undefined

        if (document.details.paymentDateOffset != null) {
            dueDate = dayjs(document.details.officialDate).add(document.details.paymentDateOffset, 'day').toDate()
        }

        return (
            <>
                <Field
                    shouldPreserveIdentity={false}
                    component={ClientInput}
                    name="client"
                    enterFullPriceEnabled={configuration.enterFullPriceEnabled}
                    onBlur={() => {
                        formik.submitForm()
                    }}
                />

                <div className="invoice-info-grid">
                    <div className="invoice-info-field invoice-info-field--half">
                        <label className="invoice-info-field__label">{t('Invoice.Table.Date.title')}</label>

                        <div className="invoice-info-field__content">
                            <InlineDateEdit
                                showTimeInput
                                className="offerion-date"
                                currentValue={document.details.officialDate}
                                onSave={({ newValue }) => {
                                    const today = dayjs().startOf('day')
                                    const isBeforeToday = dayjs(newValue).isBefore(today, 'day')

                                    if (isBeforeToday) toastWarning('Invoice.Table.Date.InvoiceMovedToPast.warningMessage')

                                    formik.setFieldValue('details.officialDate', newValue)
                                    formik.submitForm()
                                }}
                            />
                        </div>
                    </div>
                    <div className="invoice-info-field invoice-info-field--half">
                        <label className="invoice-info-field__label">{t('Invoice.Table.DateDue.title')}</label>

                        <div className="invoice-info-field__content">
                            <InlineDateEdit
                                placeholderKey={t('Invoice.Table.DateDue.placeholder')}
                                className="offerion-date date-due"
                                currentValue={dueDate}
                                onSave={({ newValue }) => {
                                    const selectedDueDate = dayjs(newValue).startOf('day')
                                    const officialDate = dayjs(document.details.officialDate).startOf('day')

                                    const paymentDateOffset = selectedDueDate.diff(officialDate, 'day')

                                    formik.setFieldValue('details.paymentDateOffset', paymentDateOffset)
                                    formik.submitForm()
                                }}
                            />
                            <OfferionErrorMessage name="details.paymentDateOffset" />
                        </div>
                    </div>
                    <div className="invoice-info-field invoice-info-field--full-right">
                        <label className="invoice-info-field__label">{t('Invoice.Table.DeliveryDate.title')}</label>
                        <div className="invoice-info-field__content">
                            <InlineDateEdit
                                placeholderKey={t('Invoice.Table.DateDue.placeholder')}
                                className="offerion-date date-due"
                                // added a fallback to eInvoiceData.deliveryDate, there seems to be a bug with delivery date on document details
                                // once the bug is fixed, using document.details.deliveryDate is preferred.
                                currentValue={document.details.deliveryDate || eInvoiceData?.deliveryDate || undefined}
                                onSave={({ newValue }) => {
                                    formik.setFieldValue('details.deliveryDate', newValue)
                                    formik.submitForm()
                                }}
                            />
                            <OfferionErrorMessage name="details.deliveryDate" />
                        </div>
                    </div>
                    <div className="invoice-info-field invoice-info-field--half">
                        <label className="invoice-info-field__label">{t('Invoice.Table.Payment.title')}</label>

                        <div className="invoice-info-field__content">
                            <Field
                                as="select"
                                className="offerion-select"
                                name="details.paymentMethod"
                                onChange={(e: any) => {
                                    formik.setFieldValue('details.paymentMethod', e.target.value)
                                    formik.submitForm()
                                }}
                            >
                                <option value="0">{t('Allaround.PaymentType.cash.title')}</option>
                                <option value="1">{t('Allaround.PaymentType.creditCard.title')}</option>
                                <option value="2">{t('Allaround.PaymentType.bankAccount.title')}</option>
                                <option value="3">{t('Allaround.PaymentType.check.title')}</option>
                                <option value="4">{t('Allaround.PaymentType.other.title')}</option>
                            </Field>
                        </div>
                    </div>
                    {rateComponent}
                </div>
            </>
        )
    }

    const borderColorClassFactory = (doc: InvoiceDocumentModel) => {
        switch (doc.details.status) {
            case ReceiptStatus.Draft:
                return 'blue'
            case ReceiptStatus.Sent:
                return 'yellow'
            case ReceiptStatus.Due:
                return 'red'
            case ReceiptStatus.Paid:
                return 'green'
            case ReceiptStatus.Cancelled:
                return 'black'
        }
    }

    const BeforeTableComponent = ({
        document,
        formik,
        printSettings: printSettigs,
        settingsComponent,
    }: {
        document: InvoiceDocumentModel
        formik: FormikProps<InvoiceDocumentModel>
        printSettings: CompanyPrintSettingsModel
        settingsComponent: JSX.Element
    }) => {
        const borderColor = borderColorClassFactory(document)

        return (
            <div className={`table__box border-left-${borderColor}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 className="table__title table__title--inline">
                        <span className="table__title-separated">{printSettigs.invoiceTitle}</span>

                        <span className="table__title-separated">
                            <InlineInputEdit
                                currentValue={document.details.number}
                                placeholderKey="Invoice.Table.InvoiceNumber.invoiceNumberPlaceholder"
                                onSave={({ newValue }) => {
                                    formik.setFieldValue('details.number', newValue)
                                    formik.submitForm()
                                }}
                            />
                        </span>
                        <OfferionTooltip
                            placement="right"
                            titleKey="Invoice.Table.InvoiceNumber.infoTitle"
                            bodyKey="Invoice.Table.InvoiceNumber.infoText"
                        />
                    </h1>
                    <div>{settingsComponent}</div>
                </div>
            </div>
        )
    }

    return (
        <>
            <BreadcrumbsItem to={location.pathname}>{pageTitle}</BreadcrumbsItem>

            <DocumentDetailsContainer
                onCancel={onCancelButtonClick}
                signatureSrc={
                    params.invoiceId ? `api/company/getInvoiceSignatureResource/${params.invoiceId}` : `api/company/getSignatureResource`
                }
                footerSrc={params.invoiceId ? `api/company/getInvoiceFooterResource/${params.invoiceId}` : `api/company/getFooterResource`}
                headerSrc={params.invoiceId ? `api/company/getInvoiceHeaderResource/${params.invoiceId}` : `api/company/getHeaderResource`}
                documentPromiseFactory={documentPromiseFactory}
                pageTitle={pageTitle}
                isFreeCompany={configuration.isFreeCompany}
                validationSchema={schema}
                editPurchasePrice={configuration.editPurchasePrice}
                canSubmit={document => !document.details.isCancelled && !document.fiscalization.isFiscalized}
                canChangeVat={configuration.canChangeVat}
                defaultVat={configuration.defaultVat}
                showProductDimensionsInfo={configuration.showProductDimensionsInfo}
                isAccountingVat={configuration.isAccountingVat}
                onSubmit={upsertDocument}
                actionButtonsComponent={ActionComponent}
                quickActionsMenuComponent={QuickActionsComponent}
                headerComponent={HeaderComponent}
                beforeTableComponent={BeforeTableComponent}
                footerComponent={FooterComponent}
                borderColorClass={borderColorClassFactory}
                currencyOverlayKey={'Invoice.Settings.invoiceCurrency'}
                customRateFormTitleKey={'Invoice.Settings.pageTitleInvoice'}
                successUpdateKey="Invoice.Save.Message.successUpdate"
                successCreateKey="Invoice.Save.Message.successCreate"
                itemsReorderable
            />
        </>
    )
}
